/**
 * record-assignment.ts — the SINGLE home for "one record, one grid" assignment (s156, BENEFICIARY-FILL-1
 * Sub-phase D pt.2(a)).
 *
 * A form can print the SAME pool of people across several mutually-exclusive grids (74A Part 6: ¶1 minors
 * / ¶2 incapable adults / ¶3 adults+charities). Capturing those as three independent multi-selects let one
 * person be selected into two grids at once — the app then silently resolved it at fill time. This module
 * replaces that with an ASSIGNMENT: every candidate has at most ONE target grid, so overlap is impossible
 * by construction rather than repaired after the fact.
 *
 * Storage contract is deliberately UNCHANGED: the assignment is fanned out to the per-grid selection keys
 * the fill/prep/echo already read (form_field_answers, latest-wins), and the assignment string itself is
 * stored alongside so the control round-trips. Nothing downstream of the capture step had to learn a new
 * shape.
 *
 * Form-agnostic (jurisdiction seam): grids opt in by declaring a shared `assignmentGroup` in the registry.
 * A province pack gets the mechanic by data-authoring — no engineering. Nothing here knows about Ontario,
 * 74A, or what any grid MEANS; the executor decides every assignment (the app computes no eligibility).
 *
 * Pure + platform-neutral (no server/client imports) so both sides consume the same functions.
 */

import { serializeMultiSelect, parseMultiSelect } from "./constants/form-field-tiers";
import { dedupeOrderedGroups } from "./repeating-group-dedupe";

/** The form_field_answers key holding the executor's assignment for one group. */
export function assignmentKey(group: string): string {
  return `${group}_assignment`;
}
/** The form_field_answers key holding the rowIds the executor confirmed as cross-source duplicates. */
export function mergedKey(group: string): string {
  return `${group}_merged`;
}

/** One selectable target of an assignment control — a grid the executor can put a candidate in. `label`
 *  is the executor-facing short name; `heading` is the form's OWN ¶ heading (ML-3 verbatim), shown so the
 *  executor decides against the form's words, not ours. */
export interface AssignTarget {
  gridKey: string;
  label: string;
  heading: string;
  note?: string;
  /** s156 D pt.2(b) — the columns of THIS grid the executor types per record (guardian/attorney details,
   *  estimated values). Only shown for a candidate actually assigned here: a question about a person's
   *  guardian is meaningless until they are on that paragraph. */
  executorColumns?: AssignColumn[];
}
export interface AssignColumn {
  /** the registry column key — one half of the per-record answer key. */
  key: string;
  /** the column heading as PRINTED on the form (ML-3 verbatim). */
  label: string;
  /** neutral guidance authored on the registry column. */
  note?: string;
  /** the printed cell spans several lines (an address block) → render a textarea. */
  multiline?: boolean;
  /** the cell already carries a planner value the executor is ADDING to (composite) — the UI says so,
   *  so the executor doesn't re-type an address the form will already print. */
  appendsTo?: string;
  /** s156 D pt.2(d) — a PLANNER-WINS fallback ask: rendered only for records whose plan value is empty
   *  (the gap's `plannerFilled` map says which records to suppress it for). A recorded planner fact is
   *  never re-asked and never overridden. */
  fallback?: boolean;
}

// ── Per-record executor-supplied cells (s156 D pt.2(b)) ──────────────────────────────────────────
// Some printed columns have NO planner source — a minor beneficiary's guardian, an estimated value of
// interest. The executor supplies them per record, at generation. They live in the SAME append-only
// answer store as every other T2 answer (form-field-answers.ts — no new storage), under a composite key
// so one (grid, record, column) cell is addressable. `~` is the separator: rowIds are UUIDs or
// `__seeded__…__N`, gridKeys and column keys are slugs — none can contain one.
const CELL_SEP = "~";
export function recordCellKey(group: string, gridKey: string, rowId: string, columnKey: string): string {
  return [group, gridKey, rowId, columnKey].join(CELL_SEP);
}
export function parseRecordCellKey(
  key: string,
): { group: string; gridKey: string; rowId: string; columnKey: string } | null {
  const p = key.split(CELL_SEP);
  if (p.length !== 4 || p.some((x) => x === "")) return null;
  return { group: p[0], gridKey: p[1], rowId: p[2], columnKey: p[3] };
}
/** True when `key` addresses a cell of THIS group (used to sweep a supplied answer bag). */
export function isRecordCellKeyFor(group: string, key: string): boolean {
  return parseRecordCellKey(key)?.group === group;
}

/** rowId → target gridKey. A candidate ABSENT from the map is "not on this form" (an explicit choice —
 *  the control offers it; an unassigned candidate is simply never printed). */
export type Assignment = Record<string, string>;

/** `rowId:gridKey` pairs, sorted, comma-joined — deterministic, same codec family as multi-selects
 *  (rowIds are generic_entries ids and gridKeys are slugs; neither can contain a comma or a colon). */
export function serializeAssignment(a: Assignment): string {
  return Object.entries(a)
    .filter(([rowId, grid]) => rowId.trim() !== "" && grid.trim() !== "")
    .map(([rowId, grid]) => `${rowId.trim()}:${grid.trim()}`)
    .sort()
    .join(",");
}

export function parseAssignment(v: string | null | undefined): Assignment {
  const out: Assignment = {};
  for (const pair of parseMultiSelect(v)) {
    const i = pair.indexOf(":");
    if (i <= 0) continue; // malformed → ignored, never guessed
    const rowId = pair.slice(0, i).trim();
    const grid = pair.slice(i + 1).trim();
    if (rowId && grid) out[rowId] = grid; // last wins within one string (can't happen from our serializer)
  }
  return out;
}

/**
 * Fan the assignment out to the per-grid selection keys the fill path reads. `orderedRowIds` is the
 * candidate pool's OWN order, so each grid's stored selection is in the same order the pool (and therefore
 * the echo, and therefore the paper) presents — the echo can never name a different order than the form.
 * Every gridKey gets an entry, including an empty one: an emptied grid must overwrite its previous
 * selection, not inherit it from the last generation (latest-wins).
 */
export function assignmentToPerGrid(
  a: Assignment,
  gridKeys: readonly string[],
  orderedRowIds: readonly string[],
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const g of gridKeys) out[g] = orderedRowIds.filter((id) => a[id] === g).join(",");
  return out;
}

/**
 * Legacy per-grid answers → an assignment (the one-time read migration for estates that answered the three
 * independent pickers before this build). Overlap is resolved with the SHARED policy the fill has always
 * used — first-declared grid wins (dedupeOrderedGroups) — so the migrated assignment shows the executor
 * exactly what the form would have printed, not a new arrangement they never chose.
 */
export function perGridToAssignment(
  answers: Record<string, string>,
  gridKeys: readonly string[],
): Assignment {
  const { perGroup } = dedupeOrderedGroups(
    gridKeys.map((g) => ({ selectionKey: g, rowIds: parseMultiSelect(answers[g]) })),
  );
  const out: Assignment = {};
  for (const g of perGroup) for (const id of g.kept) out[id] = g.selectionKey;
  return out;
}

/** The rowIds the executor CONFIRMED are duplicates of another record in the pool — excluded from the
 *  assignment control and from the fan-out, so the person is printed once. Same codec as multi-selects. */
export function serializeMerged(rowIds: readonly string[]): string {
  return serializeMultiSelect(rowIds);
}
export function parseMerged(v: string | null | undefined): string[] {
  return parseMultiSelect(v);
}

/** True once the executor has answered the group (either the new assignment key, or — for an estate that
 *  answered before this build — ANY of the per-grid keys, which migrates rather than re-nagging). */
export function assignmentAnswered(
  group: string,
  gridKeys: readonly string[],
  answers: Record<string, string>,
): boolean {
  if (assignmentKey(group) in answers) return true;
  return gridKeys.some((g) => g in answers);
}

/** The executor's current assignment for a group, from either shape (new key wins). */
export function readAssignment(
  group: string,
  gridKeys: readonly string[],
  answers: Record<string, string>,
): Assignment {
  const k = assignmentKey(group);
  if (k in answers) return parseAssignment(answers[k]);
  return perGridToAssignment(answers, gridKeys);
}

// ── Pre-sort (s156 D pt.2(c)) ─────────────────────────────────────────────────────────────────────
// A STARTING SUGGESTION for the assignment control, computed only from facts the plan already holds.
// It is not a decision and it is not eligibility: every suggestion lands in an editable control the
// executor must review and save, and a suggestion is offered ONLY on a group the executor has never
// answered (see `presortApplies`) — a stored assignment is never overwritten or second-guessed.
//
// 🔴 The app NEVER suggests the "mentally incapable" paragraph. Capacity is a legal determination about
// a living person that no data we hold can imply; suggesting it would be the app making exactly the kind
// of judgment this build refuses to make. That paragraph is only ever reached by the executor choosing it.
//
// Precedence is deliberate — most specific fact first:
//   1. a charity/organization        → the adults-and-charities paragraph (it can't be a minor)
//   2. an age at death of 18 or over → the adults paragraph (a known adult is never suggested for minors)
//   3. an age at death under 18      → the minors paragraph
//   4. no usable date of birth, but the record came from the planner's children section → minors
//   5. anything else                 → NO suggestion (the unsorted bucket — the executor decides)
// Rule 2 sits above rule 4 on purpose: an adult child must never be suggested for the minors paragraph
// just because of the section they were recorded in.
//
// 🔴 DO NOT "improve" this by re-running the pre-sort on an executor-supplied date of birth (D pt.2(d)).
// That date is only ever collected AFTER the executor has assigned the person to a paragraph — the
// assignment is already their confirmed decision, and a suggestion arriving afterwards would move a
// person the executor deliberately placed. The supplied date fills the cell on the form and does
// nothing else. Pre-sort runs once, on a never-answered group, from planner data only.

export interface PresortTargets {
  /** gridKey for "persons under 18". */
  minors: string;
  /** gridKey for "adults and charities". */
  adults: string;
}

const NON_PERSON_TYPES = new Set(["charity", "organization"]);

/**
 * Suggested gridKey for ONE record, or null when the plan doesn't make it clear (→ unsorted).
 * `age` is the record's age at the date of death (null when unknown) — passed in rather than computed
 * here so the one age implementation (shared/age.ts) stays the only one.
 */
export function presortRecord(
  record: Record<string, string>,
  age: number | null,
  targets: PresortTargets,
): string | null {
  if (NON_PERSON_TYPES.has((record.type ?? "").trim().toLowerCase())) return targets.adults;
  if (age != null) return age >= 18 ? targets.adults : targets.minors;
  if ((record.source ?? "") === "children") return targets.minors;
  return null; // unsorted — the executor decides
}

/** Suggested assignment for a whole candidate pool. Records with no clear suggestion are simply absent. */
export function presortAssignment(
  records: ReadonlyArray<Record<string, string>>,
  ages: ReadonlyArray<number | null>,
  targets: PresortTargets,
): Assignment {
  const out: Assignment = {};
  records.forEach((r, i) => {
    const g = presortRecord(r, ages[i] ?? null, targets);
    if (g) out[r.rowId] = g;
  });
  return out;
}

/** Pre-sort is offered ONLY on a group the executor has never answered. Once they have saved anything,
 *  their assignment is the truth: no suggestion is injected over it, and a record they deliberately left
 *  off the form stays off. */
export function presortApplies(
  group: string,
  gridKeys: readonly string[],
  answers: Record<string, string>,
): boolean {
  return !assignmentAnswered(group, gridKeys, answers);
}

// ── Cross-source duplicate detection (ruling 3) ───────────────────────────────────────────────────
// The same person can legitimately live in TWO planner sections (a child who is also a beneficiary), so
// the pooled candidate list can hold them twice. We SURFACE the suspicion and let the executor confirm;
// we NEVER merge silently. Matching is name-only (normalized first+surname) and deliberately naive — it
// is a prompt for a human decision, not an identity claim.

export interface DuplicateCandidate {
  rowId: string;
  /** which pool the record came from (the LIST_RESOLVERS-level origin, e.g. "beneficiaries"/"children"). */
  source: string;
  label: string;
}
export interface DuplicateGroup {
  /** the normalized name the group matched on — stable id for the confirm control. */
  key: string;
  members: DuplicateCandidate[];
}

/** Case/whitespace/punctuation-insensitive name key. Empty when there is no name to compare. */
export function normalizeNameKey(first: string, surname: string): string {
  const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, "");
  const k = `${norm(first)}|${norm(surname)}`;
  return k === "|" ? "" : k;
}

/**
 * Suspected duplicates in a pooled candidate list: records sharing a normalized first+surname AND coming
 * from MORE THAN ONE source. Two rows from the SAME source are two records the planner deliberately
 * entered separately (two grandchildren with one name) — not our business to question.
 */
export function suspectedDuplicates(
  // The LIST_RESOLVERS record shape (a flat string cell bag) — accepted as-is so no caller has to
  // re-map the pool just to run the check.
  records: ReadonlyArray<Record<string, string>>,
): DuplicateGroup[] {
  const byName = new Map<string, DuplicateCandidate[]>();
  for (const r of records) {
    const key = normalizeNameKey(r.first_name ?? "", r.surname ?? "");
    if (!key) continue;
    const label = (r.name ?? `${r.first_name ?? ""} ${r.surname ?? ""}`).trim() || "(unnamed)";
    const list = byName.get(key) ?? [];
    list.push({ rowId: r.rowId, source: r.source ?? "", label });
    byName.set(key, list);
  }
  const groups: DuplicateGroup[] = [];
  for (const [key, members] of Array.from(byName.entries())) {
    if (members.length < 2) continue;
    if (new Set(members.map((m) => m.source)).size < 2) continue; // same-source repeats are not our call
    groups.push({ key, members });
  }
  return groups;
}
